import { useEffect, useState } from "react";
import { useFirestore } from "react-redux-firebase";
import { FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE } from "../../constants/FirestoreConstant";

/**
 * Hook to fetch and manage purchase order payments from Firestore
 * @param {Object} options - Configuration options
 * @param {boolean} options.realtime - Whether to use real-time listener (default: true)
 * @param {string} options.invoiceId - Filter by invoice ID (optional)
 * @param {string} options.supplierId - Filter by supplier ID (optional)
 * @returns {Object} - Payments data and state
 */
export const usePurchaseOrderPayments = (options = {}) => {
  const { realtime = true, invoiceId = null, supplierId = null } = options;
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const firestore = useFirestore();

  useEffect(() => {
    setLoading(true);
    let query = firestore.collection(FIRESTORE_PURCHASE_ORDER_PAYMENTS_TABLE);

    // Apply filters
    if (invoiceId) {
      query = query.where("invoiceId", "==", invoiceId);
    }
    if (supplierId) {
      query = query.where("supplierId", "==", supplierId);
    }

    // Order by payment date
    query = query.orderBy("paymentDate", "desc");

    if (realtime) {
      const unsubscribe = query.onSnapshot(
        (snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setPayments(data);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error("Error fetching purchase order payments:", err);
          setError(err);
          setLoading(false);
        },
      );

      return () => unsubscribe();
    } else {
      query
        .get()
        .then((snapshot) => {
          const data = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setPayments(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching purchase order payments:", err);
          setError(err);
          setLoading(false);
        });
    }
  }, [firestore, realtime, invoiceId, supplierId]);

  return { payments, loading, error };
};
